/* eslint template-curly-spacing: 0, indent: 0 */
/* globals TEMPLATES_LIST */
import { Suspense, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { navigate, useIntl, RawIntlProvider } from 'gatsby-plugin-react-intl';
import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash';

// Components
import SEO from '../components/SEO';
import A4Container from '../components/A4Container';
import createLazyResumeTemplate from '../components/ResumeTemplateShell/createLazyResumeTemplate';

// Utils
import { fetchGithubResumeJson, isValidJsonString } from '../utils/gatsby-frontend-helpers';
import { isObjectNotEmpty, convertToToggleableObject, generateLlmPromptObject } from '../utils/utils';
import { toExportableResume, toRenderableResume } from '../utils/resume-payload';
import { getResumeBuilderExtensions, omitResumeBuilderExtensions } from '../utils/resume-builder-extensions';

// Hooks
import { useDispatch, useSelector } from '../store/StoreProvider';
import useDetectPrint from '../components/hooks/useDetectPrint';

// Actions
import setToggleableJsonResume from '../store/actions/setToggleableJsonResume';

// Translations
import templateIntls from '../intl';

// Selectors
import { selectToggleableJsonResume } from '../store/selectors';

const ResumeViewer = ({ params, uri }) => {
    const intl = useIntl();
    const [username, lang] = (params['*'] || '').split('/'); // TODO
    const template = uri.split('/').pop(); // TODO
    const isPrinting = useDetectPrint();

    const pageIntl = useMemo(() => {
        const newIntl = templateIntls.find((tempIntl) => tempIntl.locale === lang);

        if (!newIntl) {
            return templateIntls.find((tempIntl) => tempIntl.locale === intl.defaultLocale);
        }

        return newIntl;
    }, [intl.defaultLocale, lang]);

    const [resumeTemplate, setResumeTemplate] = useState(['Default']);
    const toggleableJsonResume = useSelector(selectToggleableJsonResume);
    const dispatch = useDispatch();

    const validTemplate = TEMPLATES_LIST.find((templateName) => templateName.toLowerCase() === template.toLowerCase());
    // TODO
    const hasData = isObjectNotEmpty(toggleableJsonResume);

    useEffect(() => {
        const fetchResumeJsonAndLoadTemplate = async () => {
            const jsonString = await fetchGithubResumeJson(username);
            if (!isValidJsonString(jsonString)) {
                navigate('/');
            }

            const jsonResume = JSON.parse(jsonString);
            if (!isObjectNotEmpty(jsonResume)) {
                navigate('/');
            }

            const extensions = getResumeBuilderExtensions(jsonResume);
            const standardResume = omitResumeBuilderExtensions(jsonResume);

            // The toggleable conversion strips the ignored properties, so they have to be
            // re-attached afterwards — passing them in gets them dropped instead.
            // Cover Letter is not supported for the viewer, so it is left out entirely.
            const toggleableObject = {
                ...convertToToggleableObject(cloneDeep(standardResume)),
                $schema: standardResume.$schema,
                meta: cloneDeep(standardResume.meta),
                // eslint-disable-next-line no-underscore-dangle
                __translation__: cloneDeep(extensions.translations),
                enableSourceDataDownload: extensions.enableSourceDataDownload,
                llmPrompt: generateLlmPromptObject(extensions.llmPrompt),
                careerStory: {
                    enabled: Boolean(extensions.careerStory),
                    value: extensions.careerStory,
                },
            };
            if (!isObjectNotEmpty(toggleableObject)) {
                navigate('/');
            }

            dispatch(setToggleableJsonResume(toggleableObject));
            const Template = createLazyResumeTemplate(validTemplate);
            setResumeTemplate([
                <Template
                    key={uuid()}
                    // eslint-disable-next-line no-underscore-dangle
                    customTranslations={toggleableObject.__translation__}
                    jsonResume={toRenderableResume(toggleableObject, { includeCoverLetter: false })}
                    downloadableResume={toExportableResume(toggleableObject)}
                />,
            ]);
        };

        if (!username) {
            navigate('/');
        }

        fetchResumeJsonAndLoadTemplate();
        // `isPrinting` is a dependency on purpose even though no template reads it:
        // entering print mode rebuilds the template so the anti-page-break sections
        // re-measure against the print layout.
    }, [dispatch, intl.defaultLocale, isPrinting, lang, username, validTemplate]);

    return (
        <RawIntlProvider value={pageIntl}>
            <SEO title={pageIntl.formatMessage({ id: 'resume_viewer' })} robots="noindex, nofollow" />
            <A4Container alignCenter>
                {hasData && <Suspense fallback={intl.formatMessage({ id: 'loading' })}>{resumeTemplate}</Suspense>}
                {!hasData && intl.formatMessage({ id: 'loading' })}
            </A4Container>
        </RawIntlProvider>
    );
};

ResumeViewer.propTypes = {
    params: PropTypes.shape({
        '*': PropTypes.string,
    }).isRequired,
    uri: PropTypes.string.isRequired,
};

export default ResumeViewer;
